"use client"; // ob-fix: Make this a client component for onClick handlers
import React, { useState, useEffect, useRef } from 'react';
import { User, UserInfo } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp, getDocs, getDoc, query, collection, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { useTranslation } from 'react-i18next';
import GoLiveButton from './onboarding/GoLiveButton'; // ob-fix
import OnboardingGate from './onboarding/OnboardingGate'; // ob-fix
import NewGoalsStep from './onboarding/steps/GoalsStep';
import BusinessStep from './onboarding/steps/BusinessStep';
import AvailabilityStep from './onboarding/steps/AvailabilityStep';
import SocialMediaStep from './onboarding/steps/SocialMediaStep';
import EventsStep from './onboarding/steps/EventsStep';
import NetworkclubStep from './onboarding/steps/NetworkclubStep';
import NetworkclubDetailsStep from './onboarding/steps/NetworkclubDetailsStep';
import ReviewStep from './onboarding/steps/ReviewStep';
import LanguageSelector from './LanguageSelector';
import { 
  expertiseOptions, 
  interestsOptions, 
  regionsOptions, 
  languagesOptions,
  dutchProvinces,
  dutchCities,
  dutchPostcodeAreas
} from '../data/seedData';
import GoogleCalendarConnectHook from './GoogleCalendarConnectHook'; // Hook-based component
import { ENABLE_ONBOARDING_GOALS, ENABLE_ONBOARDING_BIZ, ENABLE_AVAILABILITY } from '../config/flags';
import CommunityTerms from './CommunityTerms';
import { BusinessIcon, ProfileIcon, CalendarIcon, QuickIcon } from './icons';
import { Globe, Camera, Link as LinkIcon2, Lightbulb, Smartphone, AlertTriangle, Home } from 'lucide-react';
// import { UserProfile } from '../types';



interface OnboardingWizardProps {
  userProfile: User | null;
}

type OnboardingStep = 
  | 'account-type'
  | 'profile'
  | 'email-verify'
  | 'phone-verify'
  | 'manager-request'
  | 'networkclub'
  | 'networkclub-details'
  | 'goals' 
  | 'business' 
  | 'availability' 
  | 'socialMedia'
  | 'integrations'
  | 'events' 
  | 'terms'
  | 'review';

interface OnboardingData {
  // Account type step
  accountType: 'entrepreneur' | 'manager' | null;
  
  // Manager request (only for managers)
  managerRequest?: {
    companyName: string;
    companyKvK: string;
    companyEmail: string;
    companyPhone: string;
    message: string;
  };
  
  // Profile step
  firstName: string;
  lastName: string;
  displayName: string; // Computed from firstName + lastName
  photoURL: string;
  photoPosition: { x: number; y: number }; // Photo positioning for crop
  bio: string;
  company: string;
  companyLocation: string;
  workScope: 'lokaal' | 'nationaal' | 'internationaal';
  workScopeCountry?: string;
  expertise: string[];
  interests: string[];
  region: string;
  postcode: string; // ✅ FIX: Postcode veld toevoegen
  city: string; // ✅ FIX: Stad veld toevoegen
  country: string;
  phone: string;
  languages: string[];
  
  // Goals step
  goals: {
    networking: boolean;
    partnerships: boolean;
    clients: boolean;
    learning: boolean;
    other: string;
  };
  learningGoals: string[];

  // Networkclub step
  representsNetworkclub: boolean | null; // Legacy field
  entityType: 'networkclub' | 'community' | 'mastermind' | null;
  networkclubName: string;
  networkclubKvk: string;
  
  // Business step
  idealClient: {
    industry: string[];
    companySize: 'solo' | 'smb' | 'mid' | 'enterprise';
    region: string[];
    role: string[];
    budgetRange: 'low' | 'mid' | 'high' | 'n/a';
    description: string;
  };
  offersValue: string[];
  challenges: string[];
  
  // Availability step
  availability: {
    days: string[];
    timeBlocks: Array<{
      start: string;
      end: string;
      tz: string;
    }>;
  };
  
  // Integrations step
  integrations: {
    googleCalendar: boolean;
    outlookCalendar: boolean;
    appleCalendar: boolean;
    linkedin: boolean;
    socialShare: 'off' | '3x' | '5x' | '7x';
  };
  
  // Events step
  eventPrefs: {
    mode: 'online' | 'hybride' | 'fysiek';
    regions: string[];
    themes: string[];
    speakerIntent: boolean;
    hostIntent: boolean;
  };
  
  contentPrefs: {
    topics: string[];
    podcastExp: 'beginner' | 'ervaren' | 'pro';
    formatPrefs: ('video' | 'audio' | 'article' | 'live')[];
  };
  
  matchPrefs: {
    prefer: 'clients' | 'partners' | 'sparring';
    ambition: 'beginner' | 'groeiend' | 'scale-up' | 'expert';
    rewards: string[];
  };
  
  // Terms step
  acceptedTerms: string[];
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ userProfile }) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('account-type');
  const [data, setData] = useState<OnboardingData>({
    accountType: null,
    managerRequest: {
      companyName: '',
      companyKvK: '',
      companyEmail: '',
      companyPhone: '',
      message: ''
    },
    firstName: userProfile?.displayName?.split(' ')[0] || '',
    lastName: userProfile?.displayName?.split(' ').slice(1).join(' ') || '',
    displayName: userProfile?.displayName || '',
    photoURL: userProfile?.photoURL || '',
    photoPosition: { x: 50, y: 50 }, // Center by default
    bio: '',
    company: '',
    companyLocation: '',
    workScope: 'lokaal',
    workScopeCountry: '',
    expertise: [],
    interests: [],
    region: '',
    postcode: '', // ✅ FIX: Postcode veld toevoegen
    city: '', // ✅ FIX: Stad veld toevoegen
    country: '',
    phone: ((userProfile as any)?.phone) || userProfile?.phoneNumber || '',
    languages: [],
    goals: {
      networking: false,
      partnerships: false,
      clients: false,
      learning: false,
      other: ''
    },
    learningGoals: [],
    representsNetworkclub: null,
    entityType: null,
    networkclubName: '',
    networkclubKvk: '',
    idealClient: {
      industry: [],
      companySize: 'smb',
      region: [],
      role: [],
      budgetRange: 'mid',
      description: ''
    },
    offersValue: [],
    challenges: [],
    availability: {
      days: [],
      timeBlocks: []
    },
    integrations: {
      googleCalendar: false,
      outlookCalendar: false,
      appleCalendar: false,
      linkedin: false,
      socialShare: 'off'
    },
    eventPrefs: {
      mode: 'online',
      regions: [],
      themes: [],
      speakerIntent: false,
      hostIntent: false
    },
    contentPrefs: {
      topics: [],
      podcastExp: 'beginner',
      formatPrefs: []
    },
    matchPrefs: {
      prefer: 'clients',
      ambition: 'groeiend',
      rewards: []
    },
    acceptedTerms: []
  });
  
  const [loading, setLoading] = useState(false);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const goalsStepRef = useRef<import('./onboarding/steps/GoalsStep').GoalsStepRef | null>(null);

  // ── Email verificatie ──────────────────────────────────────────────────────
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(userProfile?.emailVerified ?? false);
  const [emailVerifyError, setEmailVerifyError] = useState<string | null>(null);
  const [emailVerifyChecking, setEmailVerifyChecking] = useState(false);

  // ── Telefoon verificatie (Firebase Phone Auth) ─────────────────────────────
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [phoneConfirmationResult, setPhoneConfirmationResult] = useState<any>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaVerifierRef = useRef<any>(null);

  // Zakelijk email domeinen — soft warning
  const PERSONAL_EMAIL_DOMAINS = ['gmail.com','googlemail.com','hotmail.com','hotmail.nl','outlook.com','outlook.nl','live.nl','live.com','yahoo.com','yahoo.nl','icloud.com','me.com'];
  const isPersonalEmail = userProfile?.email
    ? PERSONAL_EMAIL_DOMAINS.includes(userProfile.email.split('@')[1]?.toLowerCase() ?? '')
    : false;

  // Email verificatie stap overslaan voor Google/OAuth gebruikers (al geverifieerd)
  const isEmailAlreadyVerified = userProfile?.emailVerified === true;

  const verificationSteps: OnboardingStep[] = [
    ...(!isEmailAlreadyVerified ? ['email-verify'] as OnboardingStep[] : []),
  ];

  const steps: OnboardingStep[] = ENABLE_AVAILABILITY 
    ? [
        'account-type',
        ...(data.accountType === 'manager' ? ['manager-request'] as OnboardingStep[] : []),
        'profile',
        ...verificationSteps,
        'networkclub',
        ...(data.entityType !== null ? ['networkclub-details'] as OnboardingStep[] : []),
        'goals',
        'business',
        'availability',
        'socialMedia',
        'integrations',
        'events',
        'terms',
        'review'
      ]
    : [
        'account-type',
        ...(data.accountType === 'manager' ? ['manager-request'] as OnboardingStep[] : []),
        'profile',
        ...verificationSteps,
        'networkclub',
        ...(data.entityType !== null ? ['networkclub-details'] as OnboardingStep[] : []),
        'goals',
        'business',
        'socialMedia',
        'integrations',
        'events',
        'terms',
        'review'
      ];
  const currentStepIndex = steps.indexOf(currentStep);
  const progressPercentage = ((currentStepIndex + 1) / steps.length) * 100;

  useEffect(() => {
    if (currentStep === 'networkclub-details' && data.entityType === null) {
      setCurrentStep('goals');
    }
  }, [currentStep, data.entityType]);

  // Load saved onboarding data from Firestore
  useEffect(() => {
    const loadSavedData = async () => {
      if (!userProfile?.uid || dataLoaded) return;
      
      try {
        console.log('🔄 Loading saved onboarding data...');
        // Try direct document access first (UID as document ID)
        const userRef = doc(db, 'userProfiles', userProfile.uid);
        const userSnap = await getDoc(userRef);
        
        let savedData: any = null;
        if (userSnap.exists()) {
          savedData = userSnap.data();
          console.log('✅ Found saved onboarding data (direct access):', Object.keys(savedData));
        } else {
          // Fallback to query (old format)
          const userDoc = await getDocs(query(collection(db, 'userProfiles'), where('uid', '==', userProfile.uid)));
          if (!userDoc.empty) {
            savedData = userDoc.docs[0].data();
            console.log('✅ Found saved onboarding data (query):', Object.keys(savedData));
          }
        }
        
        if (savedData) {
          
          // Merge saved data with current state, preserving existing values if saved data is empty
          setData(prev => ({
            ...prev,
            // Profile fields
            firstName: savedData.firstName || prev.firstName,
            lastName: savedData.lastName || prev.lastName,
            displayName: savedData.displayName || prev.displayName,
            photoURL: savedData.photoURL || prev.photoURL,
            photoPosition: savedData.photoPosition || prev.photoPosition || { x: 50, y: 50 },
            bio: savedData.bio || prev.bio,
            company: savedData.company || prev.company,
            companyLocation: savedData.companyLocation || prev.companyLocation,
            workScope: savedData.workScope || prev.workScope,
            workScopeCountry: savedData.workScopeCountry || prev.workScopeCountry,
            expertise: savedData.expertise && savedData.expertise.length > 0 ? savedData.expertise : prev.expertise,
            interests: savedData.interests && savedData.interests.length > 0 ? savedData.interests : prev.interests,
            region: savedData.region || prev.region,
            postcode: savedData.postcode || prev.postcode,
            city: savedData.city || prev.city,
            country: savedData.country || prev.country,
            phone: savedData.phone || savedData.phoneNumber || prev.phone,
            languages: savedData.languages && savedData.languages.length > 0 ? savedData.languages : prev.languages,
            // Networkclub/Community/Mastermind
            representsNetworkclub: savedData.representsNetworkclub ?? prev.representsNetworkclub,
            entityType: savedData.entityType || prev.entityType,
            networkclubName: savedData.networkclubName || prev.networkclubName,
            networkclubKvk: savedData.networkclubKvk || prev.networkclubKvk,
            // Goals
            goals: savedData.goals ? { ...prev.goals, ...savedData.goals } : prev.goals,
            learningGoals: savedData.learningGoals && savedData.learningGoals.length > 0 ? savedData.learningGoals : prev.learningGoals,
            // Business
            idealClient: savedData.idealClient ? { ...prev.idealClient, ...savedData.idealClient } : prev.idealClient,
            offersValue: savedData.offersValue && savedData.offersValue.length > 0 ? savedData.offersValue : prev.offersValue,
            challenges: savedData.challenges && savedData.challenges.length > 0 ? savedData.challenges : prev.challenges,
            // Availability
            availability: savedData.availability ? { ...prev.availability, ...savedData.availability } : prev.availability,
            // Integrations
            integrations: savedData.integrations ? { ...prev.integrations, ...savedData.integrations } : prev.integrations,
            // Events
            eventPrefs: savedData.eventPrefs ? { ...prev.eventPrefs, ...savedData.eventPrefs } : prev.eventPrefs,
            // Content
            contentPrefs: savedData.contentPrefs ? { ...prev.contentPrefs, ...savedData.contentPrefs } : prev.contentPrefs,
            // Match
            matchPrefs: savedData.matchPrefs ? { ...prev.matchPrefs, ...savedData.matchPrefs } : prev.matchPrefs,
            // Terms
            acceptedTerms: savedData.acceptedTerms && savedData.acceptedTerms.length > 0 ? savedData.acceptedTerms : prev.acceptedTerms,
          }));
          
          setDataLoaded(true);
          console.log('✅ Onboarding data loaded successfully');
        } else {
          console.log('ℹ️ No saved onboarding data found, using defaults');
          setDataLoaded(true);
        }
      } catch (error) {
        console.error('❌ Error loading saved onboarding data:', error);
        setDataLoaded(true); // Continue even if load fails
      }
    };
    
    loadSavedData();
  }, [userProfile?.uid, dataLoaded]);

  // Auto-save data to Firestore on each step
  const saveData = async (stepData: Partial<OnboardingData>) => {
    if (!userProfile?.uid) return;
    
    try {
      // Always use UID as document ID for consistency
      const userRef = doc(db, 'userProfiles', userProfile.uid);
      const userSnap = await getDoc(userRef);
      
      // Remove undefined values before saving
      const cleanedData = Object.fromEntries(
        Object.entries(stepData).filter(([_, value]) => value !== undefined)
      );
      
      // Ensure phone is saved to both fields for compatibility (at root level)
      const dataToSave: any = {
        ...cleanedData,
        phone: cleanedData.phone || cleanedData.phoneNumber || undefined,
        phoneNumber: cleanedData.phone || cleanedData.phoneNumber || undefined,
        updatedAt: serverTimestamp()
      };
      
      // Remove undefined values again after phone merge
      const finalData = Object.fromEntries(
        Object.entries(dataToSave).filter(([_, value]) => value !== undefined && value !== '')
      );
      
      // Log what we're saving for debugging
      if (finalData.phone || finalData.phoneNumber) {
        console.log('💾 Saving phone number:', {
          phone: finalData.phone,
          phoneNumber: finalData.phoneNumber,
          allFields: Object.keys(finalData)
        });
      }
      
      if (userSnap.exists()) {
        // Update existing document
        await updateDoc(userRef, finalData as any);
        console.log('💾 Updated onboarding data:', Object.keys(finalData));
      } else {
        // Create new document if it doesn't exist
        const { setDoc } = await import('firebase/firestore');
        await setDoc(userRef, {
          uid: userProfile.uid,
          ...finalData,
          createdAt: serverTimestamp()
        });
        console.log('💾 Created new user profile with onboarding data');
      }
    } catch (error) {
      console.error('❌ Error saving onboarding data:', error);
    }
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    const newInvalidFields = new Set<string>();
    
    switch (currentStep) {
      case 'account-type':
        if (!data.accountType) {
          alert('⚠️ Kies een account type om door te gaan.');
          return Promise.resolve(false);
        }
        return Promise.resolve(true);

      case 'manager-request':
        if (!data.managerRequest?.companyName?.trim()) {
          newInvalidFields.add('managerRequest.companyName');
        }
        if (!data.managerRequest?.companyKvK?.trim()) {
          newInvalidFields.add('managerRequest.companyKvK');
        } else if (!/^[0-9]{8}$/.test(data.managerRequest.companyKvK.trim())) {
          newInvalidFields.add('managerRequest.companyKvK');
        }
        if (!data.managerRequest?.companyEmail?.trim()) {
          newInvalidFields.add('managerRequest.companyEmail');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.managerRequest.companyEmail.trim())) {
          newInvalidFields.add('managerRequest.companyEmail');
        }
        if (!data.managerRequest?.companyPhone?.trim()) {
          newInvalidFields.add('managerRequest.companyPhone');
        }
        
        setInvalidFields(newInvalidFields);
        if (newInvalidFields.size > 0) {
          alert('⚠️ Vul alle verplichte velden correct in.');
          return Promise.resolve(false);
        }
        return Promise.resolve(true);

      case 'profile':
        if (!data.firstName?.trim()) {
          newInvalidFields.add('firstName');
        }
        if (!data.lastName?.trim()) {
          newInvalidFields.add('lastName');
        }
        if (data.expertise.length === 0) {
          newInvalidFields.add('expertise');
        }
        if (data.interests.length === 0) {
          newInvalidFields.add('interests');
        }
        if (!data.company?.trim()) {
          newInvalidFields.add('company');
        }
        if (!data.companyLocation?.trim()) {
          newInvalidFields.add('companyLocation');
        }
        if (!data.phone?.trim()) {
          newInvalidFields.add('phone');
        }
        if (!data.workScope) {
          newInvalidFields.add('workScope');
        }
        if (data.workScope === 'nationaal' && !data.workScopeCountry?.trim()) {
          newInvalidFields.add('workScopeCountry');
        }
        if (!data.region?.trim()) {
          newInvalidFields.add('region');
        }
        if (!data.postcode?.trim()) {
          newInvalidFields.add('postcode');
        }
        if (!data.city?.trim()) {
          newInvalidFields.add('city');
        }
        
        setInvalidFields(newInvalidFields);
        
        if (newInvalidFields.size > 0) {
          const labels: Record<string, string> = {
            firstName: 'Voornaam',
            lastName: 'Achternaam',
            expertise: 'Expertise',
            interests: 'Interesses',
            company: 'Bedrijfsnaam',
            companyLocation: 'Vestigingslocatie',
            workScope: 'Werkgebied',
            workScopeCountry: 'Land voor nationaal werkgebied',
            phone: 'Telefoon',
            region: 'Provincie',
            postcode: 'Postcode',
            city: 'Stad'
          };
          const missingLabels = Array.from(newInvalidFields).map(f => labels[f] || f);
          setValidationMessage(`Vul de volgende verplichte velden in: ${missingLabels.join(', ')}`);
          // Scroll to first invalid field
          setTimeout(() => {
            const firstField = Array.from(newInvalidFields)[0];
            const el = document.querySelector(`[class*="border-\\[\\#7A9488\\]"]`) ||
                        document.querySelector(`[data-field="${firstField}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          return Promise.resolve(false);
        }
        setValidationMessage(null);
        return Promise.resolve(true);

      case 'email-verify':
        if (!emailVerified && !userProfile?.emailVerified) {
          setValidationMessage('Verifieer eerst je emailadres via de link in je inbox.');
          return Promise.resolve(false);
        }
        setValidationMessage(null);
        return Promise.resolve(true);

      case 'phone-verify':
        setValidationMessage(null);
        return Promise.resolve(true);

      case 'networkclub':
        // EntityType can be null (user doesn't have an organization), that's valid
        // Just clear any invalid fields
        setInvalidFields(new Set());
        return Promise.resolve(true);

      case 'networkclub-details':
        if (!data.networkclubName?.trim()) {
          newInvalidFields.add('networkclubName');
        }
        if (!data.networkclubKvk?.trim()) {
          newInvalidFields.add('networkclubKvk');
        } else if (!/^[0-9]{8}$/.test(data.networkclubKvk.trim())) {
          newInvalidFields.add('networkclubKvk');
        }
        setInvalidFields(newInvalidFields);
        if (newInvalidFields.size > 0) {
          setValidationMessage('Vul de naam en een geldig KVK-nummer (8 cijfers) in.');
          return Promise.resolve(false);
        }
        setValidationMessage(null);
        return Promise.resolve(true);
      
      case 'goals':
        // ✅ FIX: Always load from database to get the latest saved data
        // GoalsStep saves to 'profiles' collection via saveGoals
        try {
          const { loadGoals } = await import('../lib/onboarding.store');
          const goalsData = await loadGoals(userProfile?.uid || '');
          console.log('🔍 Validating learning goals:', {
            topics: goalsData.learning.topics,
            count: goalsData.learning.topics.length
          });
          
          if (!goalsData.learning.topics || goalsData.learning.topics.length === 0) {
            newInvalidFields.add('learningGoals');
            setInvalidFields(newInvalidFields);
            alert('Voeg minimaal één onderwerp toe bij "Voeg onderwerp toe..." om door te gaan.');
            return Promise.resolve(false);
          }
          setInvalidFields(new Set());
          return Promise.resolve(true);
        } catch (error) {
          console.error('Error loading goals for validation:', error);
          newInvalidFields.add('learningGoals');
          setInvalidFields(newInvalidFields);
          alert('Kon doelen niet laden. Controleer je internetverbinding en probeer het opnieuw.');
          return Promise.resolve(false);
        }
      
      case 'business':
        // Business-/ValueProp-gegevens worden in BusinessStep zelf gevalideerd en opgeslagen.
        // De wizard hoeft hier geen double validation te doen (anders blijft oude state leeg).
        setInvalidFields(new Set());
        return Promise.resolve(true);
      
      case 'terms':
        // Validate that required terms are accepted
        const requiredTermsCount = 4; // Voorwaarden 1-4 zijn verplicht
        const acceptedCount = data.acceptedTerms?.length || 0;
        if (acceptedCount < requiredTermsCount) {
          newInvalidFields.add('terms');
          setInvalidFields(newInvalidFields);
          setValidationMessage('Accepteer alle verplichte voorwaarden om door te gaan.');
          return Promise.resolve(false);
        }
        setInvalidFields(new Set());
        setValidationMessage(null);
        return Promise.resolve(true);
      
      default:
        setInvalidFields(new Set());
        setValidationMessage(null);
        return Promise.resolve(true);
    }
  };

  const handleNext = async () => {
    // Goals step: persist to DB first so validation (loadGoals) sees latest data
    if (currentStep === 'goals' && goalsStepRef.current) {
      try {
        await goalsStepRef.current.saveBeforeNext();
      } catch (e) {
        console.error('Error saving goals before next:', e);
        alert('Opslaan mislukt. Probeer het opnieuw.');
        return;
      }
    }
    // Validate current step before proceeding
    if (!(await validateCurrentStep())) {
      return;
    }

    setLoading(true);
    
    // Special handling for manager-request step
    if (currentStep === 'manager-request' && data.managerRequest && userProfile?.uid) {
      try {
        const { createManagerAccessRequest } = await import('../services/managerAccessService');
        
        const requestId = await createManagerAccessRequest(
          userProfile.uid,
          data.displayName || `${data.firstName} ${data.lastName}`,
          data.managerRequest.companyEmail,
          data.managerRequest.companyPhone,
          data.managerRequest.companyName,
          data.managerRequest.companyKvK,
          data.managerRequest.companyEmail,
          data.managerRequest.companyPhone,
          data.managerRequest.message
        );

        console.log('✅ Manager access request submitted:', requestId);
        
        // Show success message
        alert('✅ Je aanvraag is verzonden! De eigenaar van het bedrijf ontvangt een notificatie en kan je aanvraag goedkeuren.');
      } catch (error) {
        console.error('❌ Error submitting manager request:', error);
        alert('❌ Er is een fout opgetreden bij het verzenden van je aanvraag. Probeer het opnieuw.');
        setLoading(false);
        return;
      }
    }
    
    // Save all current data before proceeding
    await saveData(data);
    
    setValidationMessage(null);
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1]);
    }
    setLoading(false);
  };

  // Auto-save data when it changes (debounced to avoid too many writes)
  useEffect(() => {
    if (!dataLoaded || !userProfile?.uid) return;
    
    const timeoutId = setTimeout(() => {
      console.log('💾 Auto-saving onboarding data...');
      saveData(data).catch(error => {
        console.error('Error auto-saving:', error);
      });
    }, 2000); // Save 2 seconds after last change
    
    return () => clearTimeout(timeoutId);
  }, [data, dataLoaded, userProfile?.uid]);

  const handleBack = () => {
    setValidationMessage(null);
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1]);
    }
  };

  // ob-fix: Single responsibility button handler with idempotent completion
  // const [busy, setBusy] = useState(false);
  // const [error, setError] = useState<string>('');

  // completeOnboarding handled by GoLiveButton component

  // ── Email verificatie helpers ──────────────────────────────────────────────
  const sendVerificationEmail = async () => {
    if (!userProfile) return;
    setEmailVerifyError(null);
    try {
      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(userProfile);
      setEmailSent(true);
    } catch (e: any) {
      if (e?.code === 'auth/too-many-requests') {
        setEmailVerifyError('Te veel pogingen. Wacht even en probeer opnieuw.');
      } else {
        setEmailVerifyError('Kon verificatie-email niet verzenden. Probeer het opnieuw.');
      }
    }
  };

  const checkEmailVerified = async () => {
    if (!userProfile) return;
    setEmailVerifyChecking(true);
    setEmailVerifyError(null);
    try {
      await userProfile.reload();
      if (userProfile.emailVerified) {
        setEmailVerified(true);
      } else {
        setEmailVerifyError('Je email is nog niet geverifieerd. Klik op de link in je inbox.');
      }
    } catch {
      setEmailVerifyError('Kon verificatiestatus niet ophalen. Probeer opnieuw.');
    } finally {
      setEmailVerifyChecking(false);
    }
  };

  // ── Telefoon verificatie helpers ───────────────────────────────────────────
  const initRecaptcha = async () => {
    if (recaptchaVerifierRef.current || !recaptchaContainerRef.current) return;
    const { RecaptchaVerifier } = await import('firebase/auth');
    recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: 'invisible',
      callback: () => {},
    });
    await recaptchaVerifierRef.current.render();
  };

  const sendPhoneOtp = async () => {
    const rawPhone = phoneInput.trim();
    if (!rawPhone) { setPhoneError('Vul je telefoonnummer in.'); return; }
    // Normalize: ensure it starts with +
    const normalizedPhone = rawPhone.startsWith('+') ? rawPhone : `+31${rawPhone.replace(/^0/, '')}`;
    setPhoneSending(true);
    setPhoneError(null);
    try {
      await initRecaptcha();
      const { signInWithPhoneNumber } = await import('firebase/auth');
      const confirmation = await signInWithPhoneNumber(auth, normalizedPhone, recaptchaVerifierRef.current);
      setPhoneConfirmationResult(confirmation);
    } catch (e: any) {
      const msgs: Record<string, string> = {
        'auth/invalid-phone-number': 'Ongeldig telefoonnummer. Gebruik het formaat +31612345678.',
        'auth/too-many-requests': 'Te veel pogingen. Wacht even en probeer opnieuw.',
        'auth/quota-exceeded': 'SMS-limiet bereikt. Neem contact op met support.',
      };
      setPhoneError(msgs[e?.code] ?? 'Kon SMS niet versturen. Controleer het nummer en probeer opnieuw.');
      recaptchaVerifierRef.current = null;
    } finally {
      setPhoneSending(false);
    }
  };

  const verifyPhoneOtp = async () => {
    if (!phoneConfirmationResult || !phoneOtpCode.trim()) return;
    setPhoneSending(true);
    setPhoneError(null);
    try {
      await phoneConfirmationResult.confirm(phoneOtpCode.trim());
      setPhoneVerified(true);
      // Sla geverifieerd telefoonnummer op
      const normalized = phoneInput.trim().startsWith('+') ? phoneInput.trim() : `+31${phoneInput.trim().replace(/^0/, '')}`;
      await saveData({ phone: normalized });
    } catch (e: any) {
      setPhoneError('Verkeerde code. Controleer je SMS en probeer opnieuw.');
    } finally {
      setPhoneSending(false);
    }
  };

  // Auto-advance na email verificatie
  useEffect(() => {
    if ((emailVerified || userProfile?.emailVerified) && currentStep === 'email-verify') {
      const t = setTimeout(() => handleNext(), 1500);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailVerified, currentStep]);

  // Auto-advance na telefoon verificatie
  useEffect(() => {
    if (phoneVerified && currentStep === 'phone-verify') {
      const t = setTimeout(() => handleNext(), 1500);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneVerified, currentStep]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'account-type':
        return (
          <AccountTypeStep
            data={data}
            setData={setData}
          />
        );
      case 'manager-request':
        return (
          <ManagerRequestStep
            data={data}
            setData={setData}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
          />
        );
      case 'profile':
        return (
          <ProfileStep
            data={data}
            setData={setData}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
          />
        );
      case 'email-verify':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bevestig je emailadres</h2>
              <p className="text-gray-600 mb-1">
                We sturen een verificatielink naar:
              </p>
              <p className="font-semibold text-gray-800 mb-4">{userProfile?.email}</p>
              {isPersonalEmail && (
                <div className="mb-4 flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
                  <svg className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                  <div>
                    <p className="text-xs font-semibold text-amber-800">Privé-emailadres gedetecteerd</p>
                    <p className="text-xs text-amber-700 mt-0.5">Voor meer vertrouwen binnen het netwerk adviseren we een zakelijk emailadres te gebruiken. Je kunt doorgaan met dit adres, maar overweeg om je account later te koppelen aan je zakelijk email.</p>
                  </div>
                </div>
              )}
              {emailVerified || userProfile?.emailVerified ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-700 font-semibold">✓ Email geverifieerd</p>
                  <p className="text-green-600 text-sm mt-1">Je kunt doorgaan naar de volgende stap.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {!emailSent ? (
                    <button
                      type="button"
                      onClick={() => void sendVerificationEmail()}
                      className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
                    >
                      Verstuur verificatie-email
                    </button>
                  ) : (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-left">
                      <p className="text-blue-800 font-semibold text-sm">✉ Email verzonden!</p>
                      <p className="text-blue-700 text-sm mt-1">Klik op de link in je inbox. Check ook je spam-map.</p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => void checkEmailVerified()}
                    disabled={emailVerifyChecking}
                    className="w-full py-2.5 px-4 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                  >
                    {emailVerifyChecking ? 'Controleren…' : 'Ik heb geklikt — controleer verificatie'}
                  </button>
                  {emailSent && (
                    <button
                      type="button"
                      onClick={() => void sendVerificationEmail()}
                      className="w-full py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Opnieuw versturen
                    </button>
                  )}
                </div>
              )}
              {emailVerifyError && (
                <p className="mt-3 text-sm text-red-600">{emailVerifyError}</p>
              )}
            </div>
          </div>
        );

      case 'phone-verify':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bevestig je telefoonnummer</h2>
              <p className="text-gray-600 mb-6">
                We sturen je een SMS-code om te bevestigen dat jij het bent. Zo beschermen we jouw account en het netwerk.
              </p>
            </div>

            {/* Invisible reCAPTCHA container */}
            <div ref={recaptchaContainerRef} id="recaptcha-container" />

            {phoneVerified ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                <p className="text-green-700 font-semibold">✓ Telefoonnummer geverifieerd</p>
                <p className="text-green-600 text-sm mt-1">Je kunt doorgaan naar de volgende stap.</p>
              </div>
            ) : !phoneConfirmationResult ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Telefoonnummer</label>
                  <input
                    type="tel"
                    value={phoneInput}
                    onChange={e => { setPhoneInput(e.target.value); setPhoneError(null); }}
                    placeholder="+31 6 12345678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Gebruik het formaat +31612345678 (Nederland) of +32 voor België</p>
                </div>
                <button
                  type="button"
                  onClick={() => void sendPhoneOtp()}
                  disabled={phoneSending || !phoneInput.trim()}
                  className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  {phoneSending ? 'SMS wordt verstuurd…' : 'Stuur SMS-code'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  SMS verzonden naar {phoneInput}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Voer de 6-cijferige code in</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={phoneOtpCode}
                    onChange={e => { setPhoneOtpCode(e.target.value.replace(/\D/g, '')); setPhoneError(null); }}
                    placeholder="123456"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 text-gray-900 text-center text-xl tracking-[0.3em] font-mono"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void verifyPhoneOtp()}
                  disabled={phoneSending || phoneOtpCode.length < 6}
                  className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  {phoneSending ? 'Bevestigen…' : 'Bevestig code'}
                </button>
                <button
                  type="button"
                  onClick={() => { setPhoneConfirmationResult(null); setPhoneOtpCode(''); recaptchaVerifierRef.current = null; }}
                  className="w-full py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Ander nummer gebruiken
                </button>
              </div>
            )}
            {phoneError && (
              <p className="text-sm text-red-600 text-center">{phoneError}</p>
            )}
          </div>
        );

      case 'networkclub':
        return (
          <NetworkclubStep
            data={data}
            setData={setData}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
          />
        );
      case 'networkclub-details':
        return (
          <NetworkclubDetailsStep
            data={data}
            setData={setData}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
          />
        );
      case 'goals':
        if (ENABLE_ONBOARDING_GOALS) {
          return (
            <NewGoalsStep 
              uid={userProfile?.uid || ''}
              onNext={handleNext}
              onBack={handleBack}
              stepRef={goalsStepRef}
            />
          );
        } else {
          return (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🚧</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Goals stap in ontwikkeling</h2>
              <p className="text-gray-600 mb-6">Deze stap wordt binnenkort beschikbaar gesteld.</p>
              <div className="text-center">
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Doorgaan →
                </button>
              </div>
            </div>
          );
        }
      case 'business':
        // Force enable business step for testing
        return (
          <BusinessStep 
            uid={userProfile?.uid || ''}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'availability':
        return (
          <AvailabilityStep 
            uid={userProfile?.uid || ''}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'socialMedia':
        return (
          <SocialMediaStep 
            uid={userProfile?.uid || ''}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'integrations':
        return <IntegrationsStep data={data} setData={setData} userProfile={userProfile} />;
      case 'events':
        return (
          <EventsStep 
            uid={userProfile?.uid || ''}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'terms':
        return (
          <CommunityTerms
            mode="onboarding"
            initialAcceptedTerms={data.acceptedTerms || []}
            onAccept={async (acceptedTerms) => {
              const nextData = { ...data, acceptedTerms };
              setData(nextData);
              await saveData(nextData);
              setValidationMessage(null);
              if (currentStepIndex < steps.length - 1) {
                setCurrentStep(steps[currentStepIndex + 1]);
              }
            }}
            onClose={() => handleBack()}
            showOnlyRequired={true}
          />
        );
      case 'review':
        return (
          <ReviewStep 
            uid={userProfile?.uid || ''}
            onNext={handleNext}
            guidelinesAccepted={guidelinesAccepted}
            setGuidelinesAccepted={setGuidelinesAccepted}
          />
        );
      default:
        return null;
    }
  };

  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <OnboardingGate /> {/* ob-fix: Live listener for immediate redirect */}
      {/* Progress Bar */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold tracking-[-0.01em] text-gray-900">
                {t('onboarding.title')}
              </h1>
              <LanguageSelector showLabel={false} />
            </div>
            <span className="text-sm text-gray-600">
              {t('onboarding.progress', { percent: Math.round(progressPercentage) })}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {t('onboarding.subtitle')}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          {renderStepContent()}
          
          {/* Validation error banner */}
          {validationMessage && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-800 mb-0.5">Niet alle velden zijn ingevuld</p>
                <p className="text-sm text-red-700">{validationMessage}</p>
              </div>
              <button onClick={() => setValidationMessage(null)} className="ml-auto text-red-400 hover:text-red-600 shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={handleBack}
              disabled={currentStepIndex === 0}
              className="px-6 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← {t('common.back')}
            </button>
            
            {currentStep === 'review' ? (
              <GoLiveButton guidelinesAccepted={guidelinesAccepted} /> // ob-fix: Use dedicated client component
            ) : currentStep === 'email-verify' ? (
              // Email verify: toon Next alleen als geverifieerd
              (emailVerified || userProfile?.emailVerified) ? (
                <button onClick={handleNext} disabled={loading} className="px-8 py-3 bg-[#7A9488] hover:bg-[#4F6660] text-white rounded-lg font-medium disabled:opacity-50">
                  {loading ? t('common.loading') : `${t('common.next')} →`}
                </button>
              ) : <span />
            ) : currentStep === 'phone-verify' ? (
              // Phone verify: toon Next alleen als geverifieerd
              phoneVerified ? (
                <button onClick={handleNext} disabled={loading} className="px-8 py-3 bg-[#7A9488] hover:bg-[#4F6660] text-white rounded-lg font-medium disabled:opacity-50">
                  {loading ? t('common.loading') : `${t('common.next')} →`}
                </button>
              ) : <span />
            ) : currentStep === 'terms' ? (
              // Terms step has its own mandatory accept button; prevent bypass via global Next.
              <span />
            ) : (
              <button
                onClick={handleNext}
                disabled={loading}
                className="px-8 py-3 bg-[#7A9488] hover:bg-[#4F6660] text-white rounded-lg font-medium disabled:opacity-50"
              >
                {loading ? t('common.loading') : `${t('common.next')} →`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Image compression utility using Canvas API
const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.85): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Determine output format
        const isPNG = file.type === 'image/png';
        const outputFormat = isPNG ? 'image/png' : 'image/jpeg';
        const outputExtension = isPNG ? '.png' : '.jpg';
        
        // Convert to blob
        const blobCallback = (blob: Blob | null) => {
          if (!blob) {
            reject(new Error('Could not compress image'));
            return;
          }
          
          // Create new file from blob
          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, outputExtension), {
            type: outputFormat,
            lastModified: Date.now(),
          });
          
          console.log(`📸 Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
          resolve(compressedFile);
        };
        
        // Call toBlob with quality parameter for JPEG
        if (outputFormat === 'image/png') {
          canvas.toBlob(blobCallback, outputFormat);
        } else {
          canvas.toBlob(blobCallback, outputFormat, quality);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Could not load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };
  });
};

// Seed data is imported at the top of the file

// Multi-select component
const MultiSelect: React.FC<{
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  required?: boolean;
  isInvalid?: boolean;
}> = ({ label, options, selected, onChange, placeholder, required, isInvalid = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
    // Don't auto-close - let user select multiple options and save manually
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleDropdown();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyDown}
          className={`w-full px-4 py-2 text-left border rounded-lg focus:ring-2 focus:border-transparent bg-white hover:bg-gray-50 transition-colors cursor-pointer ${
            isInvalid
              ? 'border-red-400 bg-red-50 focus:ring-red-400'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {selected.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((item, index) => (
                <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                  {item}
                </span>
              ))}
              {selected.length > 3 && (
                <span className="text-gray-500 text-xs px-2 py-1">
                  +{selected.length - 3} meer
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">{placeholder || 'Klik om te selecteren...'}</span>
          )}
          <span className={`absolute right-3 top-2 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {/* Header with save and close buttons */}
            <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
              <div>
                <span className="text-sm font-medium text-gray-700">Selecteer opties</span>
                <div className="text-xs text-gray-500 mt-1">
                  Selecteer meerdere opties en klik op "Opslaan"
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                >
                  ✅ Opslaan
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  ✕ Sluiten
                </button>
              </div>
            </div>
            
            {/* Options */}
            {options.map((option, index) => (
              <label key={index} className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleOption(option)}
                  className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
            
            {/* Footer with selection count */}
            {selected.length > 0 && (
              <div className="sticky bottom-0 bg-gray-50 px-4 py-2 border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  {selected.length} optie{selected.length !== 1 ? 's' : ''} geselecteerd
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <p className="text-sm text-gray-500 mt-1">{selected.length} geselecteerd</p>
      )}
    </div>
  );
};

// Account Type Step - Choose between Entrepreneur or Manager
const AccountTypeStep: React.FC<{
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
}> = ({ data, setData }) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welkom bij SuperKonnected!</h2>
        <p className="text-gray-600">Wat beschrijft jouw rol het beste?</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Entrepreneur Option */}
        <button
          type="button"
          onClick={() => setData({ ...data, accountType: 'entrepreneur' })}
          className={`relative p-6 rounded-xl border-2 transition-all ${
            data.accountType === 'entrepreneur'
              ? 'border-blue-500 bg-blue-50 shadow-lg'
              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex flex-col items-center text-center space-y-3">
            <BusinessIcon size={48} weight="regular" className="text-gray-600" />
            <h3 className="text-xl font-bold text-gray-900">Ondernemer</h3>
            <p className="text-sm text-gray-600">
              Ik heb mijn eigen bedrijf of ben zelfstandig ondernemer
            </p>
            {data.accountType === 'entrepreneur' && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </button>

        {/* Manager Option */}
        <button
          type="button"
          onClick={() => setData({ ...data, accountType: 'manager' })}
          className={`relative p-6 rounded-xl border-2 transition-all ${
            data.accountType === 'manager'
              ? 'border-green-500 bg-green-50 shadow-lg'
              : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex flex-col items-center text-center space-y-3">
            <ProfileIcon size={48} weight="regular" className="text-gray-600" />
            <h3 className="text-xl font-bold text-gray-900">Manager</h3>
            <p className="text-sm text-gray-600">
              Ik werk als manager voor een bedrijf en wil toegang aanvragen
            </p>
            {data.accountType === 'manager' && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </button>
      </div>

      {data.accountType && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            {data.accountType === 'entrepreneur' ? (
              <>
                <strong>✓ Ondernemer gekozen</strong><br />
                Je krijgt direct toegang tot alle functies en kunt je profiel volledig inrichten.
              </>
            ) : (
              <>
                <strong>✓ Manager gekozen</strong><br />
                Je kunt toegang aanvragen tot een bestaand bedrijf. De eigenaar moet jouw aanvraag goedkeuren.
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

// Manager Request Step - Request access to a company
const ManagerRequestStep: React.FC<{
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
  invalidFields: Set<string>;
  setInvalidFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({ data, setData, invalidFields, setInvalidFields }) => {
  const handleChange = (field: keyof NonNullable<OnboardingData['managerRequest']>, value: string) => {
    setData({
      ...data,
      managerRequest: {
        ...data.managerRequest!,
        [field]: value
      }
    });
    // Remove from invalid fields when user starts typing
    if (value.trim()) {
      setInvalidFields(prev => {
        const next = new Set(prev);
        next.delete(`managerRequest.${field}`);
        return next;
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Vraag toegang aan 🔐</h2>
        <p className="text-gray-600">
          Vul de gegevens van het bedrijf in waar je voor werkt. De eigenaar ontvangt een notificatie en kan jouw verzoek goedkeuren.
        </p>
      </div>

      <div className="space-y-4">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bedrijfsnaam *
          </label>
          <input
            type="text"
            value={data.managerRequest?.companyName || ''}
            onChange={(e) => handleChange('companyName', e.target.value)}
            placeholder="Bijv. SuperKonnected B.V."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              invalidFields.has('managerRequest.companyName') ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {invalidFields.has('managerRequest.companyName') && (
            <p className="text-red-500 text-sm mt-1">Bedrijfsnaam is verplicht</p>
          )}
        </div>

        {/* KVK Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            KVK-nummer *
          </label>
          <input
            type="text"
            value={data.managerRequest?.companyKvK || ''}
            onChange={(e) => handleChange('companyKvK', e.target.value)}
            placeholder="12345678"
            maxLength={8}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              invalidFields.has('managerRequest.companyKvK') ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {invalidFields.has('managerRequest.companyKvK') && (
            <p className="text-red-500 text-sm mt-1">KVK-nummer is verplicht</p>
          )}
          <p className="text-xs text-gray-500 mt-1">8 cijfers zonder spaties</p>
        </div>

        {/* Company Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bedrijf E-mailadres *
          </label>
          <input
            type="email"
            value={data.managerRequest?.companyEmail || ''}
            onChange={(e) => handleChange('companyEmail', e.target.value)}
            placeholder="info@bedrijf.nl"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              invalidFields.has('managerRequest.companyEmail') ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {invalidFields.has('managerRequest.companyEmail') && (
            <p className="text-red-500 text-sm mt-1">Bedrijf e-mailadres is verplicht</p>
          )}
        </div>

        {/* Company Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bedrijf Telefoonnummer *
          </label>
          <input
            type="tel"
            value={data.managerRequest?.companyPhone || ''}
            onChange={(e) => handleChange('companyPhone', e.target.value)}
            placeholder="+31 6 12345678"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              invalidFields.has('managerRequest.companyPhone') ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {invalidFields.has('managerRequest.companyPhone') && (
            <p className="text-red-500 text-sm mt-1">Telefoonnummer is verplicht</p>
          )}
        </div>

        {/* Optional Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bericht (optioneel)
          </label>
          <textarea
            value={data.managerRequest?.message || ''}
            onChange={(e) => handleChange('message', e.target.value)}
            placeholder="Voeg een persoonlijk bericht toe aan je aanvraag..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Dit bericht wordt getoond aan de eigenaar van het bedrijf
          </p>
        </div>
      </div>

      <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
        <div className="flex items-start space-x-2">
          <AlertTriangle size={20} className="text-[#7A9488] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#4F6660]">
            <strong>Let op:</strong> De eigenaar van het bedrijf moet jouw aanvraag goedkeuren voordat je toegang krijgt. 
            Je ontvangt een notificatie zodra er een beslissing is genomen.
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile step implementation
const ProfileStep: React.FC<{
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
  invalidFields: Set<string>;
  setInvalidFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({ data, setData, invalidFields, setInvalidFields }) => {
  const [linkedinAutoFillLoaded, setLinkedinAutoFillLoaded] = useState(false);
  const [showImagePositioner, setShowImagePositioner] = useState(false);
  const [tempPhotoURL, setTempPhotoURL] = useState<string>('');
  const [tempPhotoFile, setTempPhotoFile] = useState<File | null>(null);
  const [tempPhotoPosition, setTempPhotoPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Auto-fill LinkedIn data if user logged in with LinkedIn
  useEffect(() => {
    const autoFillLinkedInData = async () => {
      try {
        // Check if user has LinkedIn provider data
        const user = auth.currentUser;
        if (!user || linkedinAutoFillLoaded) return;

        const isLinkedInUser = user.providerData.some((provider: UserInfo) => provider.providerId === 'linkedin.com');
        if (!isLinkedInUser) return;

        console.log('🔗 LinkedIn user detected, auto-filling profile data...');

        // Auto-fill basic info from LinkedIn auth
        const displayName = user.displayName || '';
        const [firstName, ...lastNameParts] = displayName.split(' ');
        const lastName = lastNameParts.join(' ');

        setData({
          ...data,
          firstName: firstName || data.firstName,
          lastName: lastName || data.lastName,
          displayName: displayName || data.displayName,
          photoURL: user.photoURL || data.photoURL,
          country: data.country || 'Nederland'
        });

        setLinkedinAutoFillLoaded(true);
        console.log('✅ LinkedIn data auto-filled successfully');

      } catch (error) {
        console.error('❌ Error auto-filling LinkedIn data:', error);
      }
    };

    if (!linkedinAutoFillLoaded) {
      autoFillLinkedInData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Block body scroll when image positioner modal is open
  useEffect(() => {
    if (showImagePositioner) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Block scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showImagePositioner]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('❌ Alleen afbeeldingen zijn toegestaan.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ Bestand is te groot. Maximaal 5MB toegestaan.');
      return;
    }

    // Read the file and open the image positioner
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setTempPhotoURL(event.target.result as string);
        setTempPhotoFile(file); // Store the actual file for upload
        setTempPhotoPosition(data.photoPosition || { x: 50, y: 50 });
        setShowImagePositioner(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImagePositionSave = async () => {
    if (!tempPhotoFile) {
      // If no new file, just save position of existing photo
      setData({
        ...data,
        photoPosition: tempPhotoPosition
      });
      setShowImagePositioner(false);
      return;
    }

    const user = auth.currentUser;
    if (!user?.uid) {
      alert('❌ Je moet ingelogd zijn om een foto te uploaden.');
      return;
    }

    setUploading(true);
    try {
      // Compress image before upload (max 800px for profile photos)
      console.log('🔄 Compressing image...');
      const compressedFile = await compressImage(tempPhotoFile, 800, 0.85);
      console.log('✅ Image compressed successfully');

      // Upload to Firebase Storage (using profilePhotos path that exists in storage rules)
      const photoRef = ref(storage, `profilePhotos/${user.uid}/${Date.now()}-${compressedFile.name}`);
      await uploadBytes(photoRef, compressedFile);
      const downloadURL = await getDownloadURL(photoRef);

      // Update state with uploaded photo URL and position
      setData({
        ...data,
        photoURL: downloadURL,
        photoPosition: tempPhotoPosition
      });

      console.log('✅ Photo uploaded successfully:', downloadURL);
      setShowImagePositioner(false);
      setTempPhotoFile(null);
      setTempPhotoURL('');
    } catch (error: any) {
      console.error('❌ Error uploading photo:', error);
      alert('❌ Er is een fout opgetreden bij het uploaden van de foto. Probeer het opnieuw.');
    } finally {
      setUploading(false);
    }
  };

  const handleImagePositionCancel = () => {
    setTempPhotoURL('');
    setTempPhotoFile(null);
    setShowImagePositioner(false);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !imageRef.current) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    
    // Calculate position as percentage
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Clamp between 0 and 100
    setTempPhotoPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2"><Camera size={24} className="text-gray-600" /> Jouw Profiel</h2>
        <p className="text-gray-600">Hoe completer je profiel, hoe beter je matches en kansen</p>
        
        {/* LinkedIn Auto-fill Indicator */}
        {linkedinAutoFillLoaded && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <LinkIcon2 size={20} className="text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">LinkedIn data automatisch ingevuld!</p>
                <p className="text-xs text-blue-600">Je naam en profielfoto zijn overgenomen van LinkedIn. Controleer en vul aan waar nodig.</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Photo Upload */}
      <div className="flex items-center space-x-6">
        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden relative">
          {data.photoURL ? (
            <img 
              src={data.photoURL} 
              alt="Profile" 
              className="w-full h-full object-cover" 
              style={{
                objectPosition: `${data.photoPosition.x}% ${data.photoPosition.y}%`
              }}
            />
          ) : (
            <Camera size={32} className="text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profielfoto *
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="text-xs text-gray-500 mt-1">Upload een duidelijke profielfoto</p>
          {data.photoURL && (
            <button
              type="button"
              onClick={() => {
                setTempPhotoURL(data.photoURL);
                setTempPhotoPosition(data.photoPosition);
                setShowImagePositioner(true);
              }}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              📐 Positie aanpassen
            </button>
          )}
        </div>
      </div>

      {/* Image Positioner Modal */}
      {showImagePositioner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📐 Positioneer je foto</h3>
            <p className="text-sm text-gray-600 mb-4">
              Sleep de foto om de beste positie te vinden voor de circulaire crop
            </p>
            
            {/* Image Positioner */}
            <div className="mb-6">
              <div 
                className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-move select-none"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  ref={imageRef}
                  src={tempPhotoURL}
                  alt="Position"
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%`
                  }}
                  draggable={false}
                />
                {/* Circle Overlay to show crop area */}
                <div className="absolute inset-0 pointer-events-none">
                  <svg width="100%" height="100%" className="absolute inset-0">
                    <defs>
                      <mask id="circleMask">
                        <rect width="100%" height="100%" fill="white" opacity="0.7"/>
                        <circle cx="50%" cy="50%" r="40%" fill="black"/>
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="black" opacity="0.5" mask="url(#circleMask)"/>
                    <circle cx="50%" cy="50%" r="40%" fill="none" stroke="white" strokeWidth="2" strokeDasharray="5,5"/>
                  </svg>
                </div>
                {/* Center crosshair */}
                <div 
                  className="absolute w-3 h-3 border-2 border-white rounded-full shadow-lg pointer-events-none"
                  style={{
                    left: `${tempPhotoPosition.x}%`,
                    top: `${tempPhotoPosition.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </div>
              
              {/* Preview */}
              <div className="mt-4 flex items-center justify-center space-x-4">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-2">Preview</p>
                  <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden">
                    <img
                      src={tempPhotoURL}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleImagePositionCancel}
                disabled={uploading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleImagePositionSave}
                disabled={uploading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploaden...
                  </>
                ) : (
                  'Opslaan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Voornaam *
          </label>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => {
              const firstName = e.target.value;
              setData({
                ...data, 
                firstName,
                displayName: `${firstName} ${data.lastName}`.trim()
              });
              // Remove from invalid fields when user starts typing
              if (invalidFields.has('firstName')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('firstName');
                setInvalidFields(newInvalid);
              }
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('firstName')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Je voornaam"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Achternaam *
          </label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => {
              const lastName = e.target.value;
              setData({
                ...data, 
                lastName,
                displayName: `${data.firstName} ${lastName}`.trim()
              });
              // Remove from invalid fields when user starts typing
              if (invalidFields.has('lastName')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('lastName');
                setInvalidFields(newInvalid);
              }
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('lastName')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Je achternaam"
            required
          />
        </div>
      </div>

      {/* Company Information */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bedrijfsnaam *
          </label>
          <input
            type="text"
            value={data.company}
            onChange={(e) => {
              setData({...data, company: e.target.value});
              // Remove from invalid fields when user starts typing
              if (invalidFields.has('company')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('company');
                setInvalidFields(newInvalid);
              }
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('company')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Je bedrijfsnaam"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vestigingslocatie *
          </label>
          <input
            type="text"
            value={data.companyLocation}
            onChange={(e) => {
              setData({...data, companyLocation: e.target.value});
              // Remove from invalid fields when user starts typing
              if (invalidFields.has('companyLocation')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('companyLocation');
                setInvalidFields(newInvalid);
              }
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('companyLocation')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Stad, Land"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Telefoonnummer *
          </label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => {
              setData({...data, phone: e.target.value});
              if (invalidFields.has('phone')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('phone');
                setInvalidFields(newInvalid);
              }
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('phone')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="+31 6 12345678"
            required
          />
        </div>
      </div>

      {/* Work Scope */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Werkgebied *
        </label>
        <div className={`grid grid-cols-3 gap-3 ${invalidFields.has('workScope') ? 'p-2 border-2 border-red-400 rounded-lg bg-red-50' : ''}`}>
          <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
            data.workScope === 'lokaal' 
              ? 'border-blue-500 bg-blue-50 text-blue-700' 
              : invalidFields.has('workScope')
              ? 'border-red-400 hover:border-red-400'
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="workScope"
              value="lokaal"
              checked={data.workScope === 'lokaal'}
              onChange={(e) => {
                setData({...data, workScope: e.target.value as 'lokaal' | 'nationaal' | 'internationaal'});
                if (invalidFields.has('workScope')) {
                  const newInvalid = new Set(invalidFields);
                  newInvalid.delete('workScope');
                  setInvalidFields(newInvalid);
                }
              }}
              className="sr-only"
            />
            <div className="text-center w-full">
              <Home size={24} className="text-gray-600 mb-1" />
              <div className="font-medium">Lokaal</div>
              <div className="text-xs text-gray-500">Stad/regio</div>
            </div>
          </label>
          
          <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
            data.workScope === 'nationaal' 
              ? 'border-blue-500 bg-blue-50 text-blue-700' 
              : invalidFields.has('workScope')
              ? 'border-red-400 hover:border-red-400'
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="workScope"
              value="nationaal"
              checked={data.workScope === 'nationaal'}
              onChange={(e) => {
                setData({...data, workScope: e.target.value as 'lokaal' | 'nationaal' | 'internationaal'});
                if (invalidFields.has('workScope')) {
                  const newInvalid = new Set(invalidFields);
                  newInvalid.delete('workScope');
                  setInvalidFields(newInvalid);
                }
              }}
              className="sr-only"
            />
            <div className="text-center w-full">
              <div className="text-2xl mb-1">🇳🇱</div>
              <div className="font-medium">Nationaal</div>
              <div className="text-xs text-gray-500">Heel Nederland</div>
            </div>
          </label>
          
          <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
            data.workScope === 'internationaal' 
              ? 'border-blue-500 bg-blue-50 text-blue-700' 
              : invalidFields.has('workScope')
              ? 'border-red-400 hover:border-red-400'
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="workScope"
              value="internationaal"
              checked={data.workScope === 'internationaal'}
              onChange={(e) => {
                setData({...data, workScope: e.target.value as 'lokaal' | 'nationaal' | 'internationaal'});
                if (invalidFields.has('workScope')) {
                  const newInvalid = new Set(invalidFields);
                  newInvalid.delete('workScope');
                  setInvalidFields(newInvalid);
                }
              }}
              className="sr-only"
            />
            <div className="text-center w-full">
              <Globe size={24} className="text-gray-600 mb-1" />
              <div className="font-medium">Internationaal</div>
              <div className="text-xs text-gray-500">Wereldwijd</div>
            </div>
          </label>
        </div>
      </div>

      {/* Work Scope Country (only show when nationaal is selected) */}
      {data.workScope === 'nationaal' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Land voor nationaal werkgebied *
          </label>
          <select
            value={data.workScopeCountry}
            onChange={(e) => {
              setData({...data, workScopeCountry: e.target.value});
              if (invalidFields.has('workScopeCountry')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('workScopeCountry');
                setInvalidFields(newInvalid);
              }
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('workScopeCountry')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            required
          >
            <option value="">Selecteer een land...</option>
            <option value="Nederland">🇳🇱 Nederland</option>
            <option value="België">🇧🇪 België</option>
            <option value="Duitsland">🇩🇪 Duitsland</option>
            <option value="Frankrijk">🇫🇷 Frankrijk</option>
            <option value="Verenigd Koninkrijk">🇬🇧 Verenigd Koninkrijk</option>
            <option value="Spanje">🇪🇸 Spanje</option>
            <option value="Italië">🇮🇹 Italië</option>
            <option value="Oostenrijk">🇦🇹 Oostenrijk</option>
            <option value="Zwitserland">🇨🇭 Zwitserland</option>
            <option value="Denemarken">🇩🇰 Denemarken</option>
            <option value="Zweden">🇸🇪 Zweden</option>
            <option value="Noorwegen">🇳🇴 Noorwegen</option>
            <option value="Finland">🇫🇮 Finland</option>
            <option value="Polen">🇵🇱 Polen</option>
            <option value="Tsjechië">🇨🇿 Tsjechië</option>
            <option value="Hongarije">🇭🇺 Hongarije</option>
            <option value="Portugal">🇵🇹 Portugal</option>
            <option value="Griekenland">🇬🇷 Griekenland</option>
            <option value="Ierland">🇮🇪 Ierland</option>
            <option value="Luxemburg">🇱🇺 Luxemburg</option>
            <option value="Andere">🌍 Andere</option>
          </select>
        </div>
      )}

      {/* Bio */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bio (max 240 karakters)
        </label>
        <textarea
          value={data.bio}
          onChange={(e) => setData({...data, bio: e.target.value})}
          maxLength={240}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Vertel in het kort wie je bent en wat je doet..."
        />
        <p className="text-sm text-gray-500 mt-1">{data.bio.length}/240 karakters</p>
      </div>

      {/* Expertise */}
      <MultiSelect
        label="Expertise"
        options={expertiseOptions}
        selected={data.expertise}
        onChange={(expertise) => {
          setData({...data, expertise});
          // Remove from invalid fields when user selects
          if (invalidFields.has('expertise')) {
            const newInvalid = new Set(invalidFields);
            newInvalid.delete('expertise');
            setInvalidFields(newInvalid);
          }
        }}
        placeholder="Selecteer je expertisegebieden..."
        required
        isInvalid={invalidFields.has('expertise')}
      />

      {/* Interests */}
      <MultiSelect
        label="Interesses"
        options={interestsOptions}
        selected={data.interests}
        onChange={(interests) => {
          setData({...data, interests});
          // Remove from invalid fields when user selects
          if (invalidFields.has('interests')) {
            const newInvalid = new Set(invalidFields);
            newInvalid.delete('interests');
            setInvalidFields(newInvalid);
          }
        }}
        placeholder="Wat interesseert je?"
        required
        isInvalid={invalidFields.has('interests')}
      />

      {/* Postcode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Postcode *
        </label>
        <input
          type="text"
          value={data.postcode}
          onChange={(e) => {
            // Format: 1234AB (4 digits + 2 letters)
            let value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
            if (value.length > 4) {
              value = value.slice(0, 4) + ' ' + value.slice(4, 6);
            }
            setData({...data, postcode: value});
            // Remove from invalid fields when user starts typing
            if (invalidFields.has('postcode')) {
              const newInvalid = new Set(invalidFields);
              newInvalid.delete('postcode');
              setInvalidFields(newInvalid);
            }
          }}
          placeholder="1234 AB"
          maxLength={7}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
            invalidFields.has('postcode')
              ? 'border-red-400 bg-red-50 focus:ring-red-400'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          required
        />
        <p className="text-xs text-gray-500 mt-1">Voer je postcode in (bijv. 1234 AB)</p>
      </div>

      {/* City */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Stad *
        </label>
        <div className="relative">
          <input
            type="text"
            list="dutch-cities-list"
            value={data.city}
            onChange={(e) => {
              setData({...data, city: e.target.value});
              // Remove from invalid fields when user starts typing
              if (invalidFields.has('city')) {
                const newInvalid = new Set(invalidFields);
                newInvalid.delete('city');
                setInvalidFields(newInvalid);
              }
            }}
            placeholder="Typ of selecteer je stad..."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
              invalidFields.has('city')
                ? 'border-red-400 bg-red-50 focus:ring-red-400'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            required
          />
          <datalist id="dutch-cities-list">
            {dutchCities.map((city, index) => (
              <option key={index} value={city} />
            ))}
          </datalist>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Typ je stad of selecteer uit de lijst. Als je stad er niet tussen staat, typ je de naam handmatig in.
        </p>
      </div>

      {/* Region - Nederlandse provincies */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Provincie *
        </label>
        <select
          value={data.region}
          onChange={(e) => {
            setData({...data, region: e.target.value});
            // Remove from invalid fields when user selects
            if (invalidFields.has('region')) {
              const newInvalid = new Set(invalidFields);
              newInvalid.delete('region');
              setInvalidFields(newInvalid);
            }
          }}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
            invalidFields.has('region')
              ? 'border-red-400 bg-red-50 focus:ring-red-400'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          required
        >
          <option value="">Selecteer je provincie...</option>
          <optgroup label="Nederlandse Provincies">
            {dutchProvinces.map((province, index) => (
              <option key={index} value={province}>{province}</option>
            ))}
          </optgroup>
          <optgroup label="Algemene Regio's">
            {regionsOptions.filter(r => r.includes('Nederland -')).map((region, index) => (
              <option key={index} value={region}>{region}</option>
            ))}
          </optgroup>
          <optgroup label="Internationaal">
            {regionsOptions.filter(r => !r.includes('Nederland')).map((region, index) => (
              <option key={index} value={region}>{region}</option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Country */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Land
        </label>
        <select
          value={data.country}
          onChange={(e) => setData({...data, country: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Selecteer land...</option>
          <option value="Nederland">🇳🇱 Nederland</option>
          <option value="België">🇧🇪 België</option>
          <option value="Duitsland">🇩🇪 Duitsland</option>
          <option value="Frankrijk">🇫🇷 Frankrijk</option>
          <option value="Verenigd Koninkrijk">🇬🇧 Verenigd Koninkrijk</option>
          <option value="Andere">🌍 Andere</option>
        </select>
      </div>

      {/* Languages */}
      <MultiSelect
        label="Talen"
        options={languagesOptions}
        selected={data.languages}
        onChange={(languages) => setData({...data, languages})}
        placeholder="Welke talen spreek je?"
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-1 flex items-center gap-1.5"><Lightbulb size={16} className="text-blue-700" /> Tip</h3>
        <p className="text-blue-700 text-sm">
          Een compleet profiel krijgt 3x meer matches en connecties. Vul zoveel mogelijk velden in!
        </p>
      </div>
    </div>
  );
};

// GoalsStep is now imported from ./onboarding/steps/GoalsStep
// BusinessStep is now imported from ./onboarding/steps/BusinessStep
// AvailabilityStep is now imported from ./onboarding/steps/AvailabilityStep

const IntegrationsStep: React.FC<{data: OnboardingData, setData: (data: OnboardingData) => void, userProfile: User | null}> = ({ data, setData, userProfile }) => {
  const [connectedCalendars, setConnectedCalendars] = useState({
    google: false,
    outlook: false,
    apple: false
  });
  const [calendarErrors, setCalendarErrors] = useState<Record<string, string>>({});

  const handleGoogleCalendarSuccess = () => {
    setConnectedCalendars(prev => ({ ...prev, google: true }));
    setData({
      ...data,
      integrations: {
        ...data.integrations,
        googleCalendar: true
      }
    });
    setCalendarErrors(prev => {
      const { google, ...rest } = prev;
      return rest;
    });
  };

  const handleGoogleCalendarError = (error: string) => {
    setCalendarErrors(prev => ({ ...prev, google: error }));
  };

  const handleOutlookCalendarConnect = async () => {
    try {
      const { connectOutlookCalendar } = await import('../utils/outlookCalendar');
      await connectOutlookCalendar();
      setConnectedCalendars(prev => ({ ...prev, outlook: true }));
      setData({
        ...data,
        integrations: {
          ...data.integrations,
          outlookCalendar: true
        }
      });
    } catch (error: any) {
      setCalendarErrors(prev => ({ ...prev, outlook: error.message || 'Verbinding mislukt' }));
    }
  };

  const handleAppleCalendarConnect = async () => {
    const icsUrl = prompt('Voer je Apple Calendar ICS URL in (te vinden in Instellingen → Accounts):');
    if (!icsUrl) return;

    try {
      const { connectAppleCalendar } = await import('../utils/appleCalendar');
      await connectAppleCalendar(icsUrl, 'Apple Calendar');
      setConnectedCalendars(prev => ({ ...prev, apple: true }));
      setData({
        ...data,
        integrations: {
          ...data.integrations,
          appleCalendar: true
        }
      });
    } catch (error: any) {
      setCalendarErrors(prev => ({ ...prev, apple: error.message || 'Verbinding mislukt' }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2"><CalendarIcon size={24} weight="regular" className="text-gray-700" /> Agenda Integraties</h2>
        <p className="text-gray-600">Koppel je agenda om dubbelboekingen te voorkomen en met één klik te kunnen plannen</p>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Google Calendar */}
        <div className={`p-5 border-2 rounded-xl transition-all ${
          connectedCalendars.google 
            ? 'border-green-400 bg-green-50' 
            : 'border-gray-200 bg-white hover:border-blue-300'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Google</h3>
            </div>
            {connectedCalendars.google && (
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
          
          {!connectedCalendars.google ? (
            <>
              <p className="text-sm text-gray-600 mb-3">Google Calendar</p>
              <GoogleCalendarConnectHook
                onSuccess={handleGoogleCalendarSuccess}
                onError={handleGoogleCalendarError}
                userUid={userProfile?.uid || ''}
              />
              {calendarErrors.google && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  {calendarErrors.google}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-sm font-medium text-green-700">✓ Verbonden</p>
              <p className="text-xs text-gray-600 mt-1">Je agenda wordt gesynchroniseerd</p>
            </div>
          )}
        </div>

        {/* Outlook Calendar */}
        <div className={`p-5 border-2 rounded-xl transition-all ${
          connectedCalendars.outlook 
            ? 'border-green-400 bg-green-50' 
            : 'border-gray-200 bg-white hover:border-blue-300'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 2v20l10-10L7 2z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Outlook</h3>
            </div>
            {connectedCalendars.outlook && (
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
          
          {!connectedCalendars.outlook ? (
            <>
              <p className="text-sm text-gray-600 mb-3">Microsoft Outlook Calendar</p>
              <button
                onClick={handleOutlookCalendarConnect}
                className="w-full py-2 px-3 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors"
              >
                Koppel Outlook
              </button>
              <button
                onClick={() => {}}
                className="w-full mt-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Later instellen
              </button>
              {calendarErrors.outlook && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  {calendarErrors.outlook}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-sm font-medium text-green-700">✓ Verbonden</p>
              <p className="text-xs text-gray-600 mt-1">Je agenda wordt gesynchroniseerd</p>
            </div>
          )}
        </div>

        {/* Apple Calendar */}
        <div className={`p-5 border-2 rounded-xl transition-all ${
          connectedCalendars.apple 
            ? 'border-green-400 bg-green-50' 
            : 'border-gray-200 bg-white hover:border-blue-300'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Apple</h3>
            </div>
            {connectedCalendars.apple && (
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
          
          {!connectedCalendars.apple ? (
            <>
              <p className="text-sm text-gray-600 mb-3">Apple Calendar (iCloud)</p>
              <button
                onClick={handleAppleCalendarConnect}
                className="w-full py-2 px-3 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors"
              >
                Koppel Apple
              </button>
              <button
                onClick={() => {}}
                className="w-full mt-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Later instellen
              </button>
              {calendarErrors.apple && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  {calendarErrors.apple}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-sm font-medium text-green-700">✓ Verbonden</p>
              <p className="text-xs text-gray-600 mt-1">Je agenda wordt gesynchroniseerd</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Boxes */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-1 flex items-center gap-1.5"><Smartphone size={16} className="text-blue-700" /> Social Media</h3>
        <p className="text-blue-700 text-sm">
          LinkedIn, Twitter, Instagram en andere social media koppelingen worden in de volgende stap ingesteld.
        </p>
      </div>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
        <h3 className="font-medium text-[#4A5A3D] mb-1 flex items-center gap-1.5"><QuickIcon size={16} weight="regular" className="text-[#4F6660]" /> Tip</h3>
        <p className="text-[#4F6660] text-sm">
          Je hoeft niet alle agenda's te koppelen. Kies de agenda die je het meest gebruikt. Je kunt later altijd meer toevoegen!
        </p>
      </div>

      {/* Summary */}
      {Object.values(connectedCalendars).some(connected => connected) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-green-900 mb-1">Prima gedaan!</h4>
              <p className="text-sm text-green-800">
                Je hebt {Object.values(connectedCalendars).filter(Boolean).length} agenda{Object.values(connectedCalendars).filter(Boolean).length > 1 ? "'s" : ''} gekoppeld. 
                Je kunt nu afspraken synchroniseren en dubbelboekingen voorkomen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// EventsStep and ReviewStep are now imported from separate files

// Duplicate return statement removed - using original return statement

export default OnboardingWizard;
